<script lang="ts" setup>
defineProps<{
  total: number
  // Fractional "current page" — integer means we're sitting on a page,
  // a value like 0.6 means we're 60 % of the way swiped from page 0
  // toward page 1. ReadingStage derives this from `pageIndex` plus
  // the in-flight `pageOffset`.
  current: number
}>()
</script>

<template>
  <!--
    Horizontal Stories-style progress bar pinned at the top of the
    reading stage. One dash per page; each dash fills from the left as
    `current` slides across its index, so the bar reads as smoothly
    animated during page transitions (rather than snapping at the
    threshold).
  -->
  <div class="progress-bar" :style="{ '--progress-current': current }">
    <div
      v-for="i in total"
      :key="i"
      class="progress-dash"
      :style="{ '--progress-index': i - 1 }"
    />
  </div>
</template>

<style scoped lang="scss">
.progress-bar {
  display: flex;
  gap: 6px;
  left: 16px;
  margin: 0 auto;
  max-width: 400px;
  pointer-events: none;
  position: absolute;
  right: 16px;

  // Respect the iOS notch / status bar inset when present, otherwise
  // fall back to a sensible 16px from the top edge.
  top: max(16px, env(safe-area-inset-top, 16px));
  z-index: 30;
}

// Dim background = `--font-color` mixed 75% toward transparent so the
// foreground fill (::after) can paint over it at full strength without
// the parent's opacity dimming it back down.
.progress-dash {
  background: color-mix(in srgb, var(--font-color, var(--text-dark)), transparent 75%);
  border-radius: 8px;
  flex: 1;
  height: 3px;
  overflow: hidden;
  position: relative;
}

// Filled portion. Width = `current - index + 1`, clamped to [0%, 100%]:
//   current === index           → 100 % (this page has been reached)
//   current === index - 1       → 0 %   (next page, not yet)
//   in between                  → partial fill that follows the drag.
// `--progress-fill-transition` is set by ReadingStage — only non-`none`
// while `isSnapping` is true, so the fill follows the finger at 1:1
// during an active drag but smoothly catches up during the snap.
.progress-dash::after {
  background: var(--font-color, var(--text-dark));
  bottom: 0;
  content: "";
  left: 0;
  position: absolute;
  top: 0;
  transition: var(--progress-fill-transition, none);
  width: clamp(0%, calc((var(--progress-current) - var(--progress-index) + 1) * 100%), 100%);
}
</style>
